import {
  exportStudioBreakdownCsv,
  exportStudioBreakdownExcel,
  toNum,
  type IFixedBreakdownRow,
  type IStudioBreakdownBlock,
  type IStudioBreakdownExcelConfig,
} from '@sg/utils/studioBreakdownExcel';

export interface IStaffRateBreakdownItem {
  level1: string;
  level2: string;
  level3?: string | null;
  value?: number | null;
}

export interface IStaffRateBreakdownMonthRow {
  date: string;
  source?: string;
  is_actual?: number | null;
  items?: IStaffRateBreakdownItem[];
}

/** Portfolio Overview download: one studio with nested month series. */
export interface IStaffRatePortfolioStudioRow {
  studio_id?: string;
  studio_name: string;
  series?: IStaffRateBreakdownMonthRow[];
}

export interface IExportStaffRateExcelParams {
  datatime: string;
  name?: string;
  fileFormat?: 'excel' | 'csv';
  /** Studios page: flat month rows for one studio. */
  rawData?: IStaffRateBreakdownMonthRow[];
  studioName?: string;
  /** Portfolio Overview: nested studios → series months. */
  studios?: IStaffRatePortfolioStudioRow[];
}

const normalizeLevel = (value?: string | null): string => (
  // API may omit level3; treat null/blank as empty string for stable item matching.
  value?.trim() || ''
);

const findItemValue = (
  monthRow: IStaffRateBreakdownMonthRow,
  level1: string,
  level2: string,
  level3: string,
): number | null => {
  const target = {
    level1: normalizeLevel(level1),
    level2: normalizeLevel(level2),
    level3: normalizeLevel(level3),
  };
  const matchedItem = (monthRow.items || []).find((item) => (
    normalizeLevel(item.level1) === target.level1
    && normalizeLevel(item.level2) === target.level2
    && normalizeLevel(item.level3) === target.level3
  ));
  return toNum(matchedItem?.value);
};

const STAFF_RATE_FIXED_ROW_DEFS: Array<{
  level1: string;
  level2: string;
  level3: string;
}> = [
  {
    level1: 'R&D Cost',
    level2: 'Development cost recognized in COPS',
    level3: 'Salary & Welfare',
  },
  {
    level1: 'R&D Cost',
    level2: 'Development cost recognized in G&A',
    level3: 'Salary & Welfare',
  },
  {
    level1: 'S&M Cost',
    level2: 'Selling and marketing expenses',
    level3: 'Salary & Welfare',
  },
  {
    level1: 'G&A - Non R&D expense',
    level2: 'G&A - Non R&D expense',
    level3: 'Salary & Welfare',
  },
  {
    level1: 'HC',
    level2: 'Total FTE HC',
    level3: '',
  },
  {
    level1: 'Staff Rate',
    level2: 'Staff Rate',
    level3: '',
  },
];

const buildStaffRateFixedRows = (): IFixedBreakdownRow<IStaffRateBreakdownMonthRow>[] => (
  STAFF_RATE_FIXED_ROW_DEFS.map((rowDef) => ({
    level1: rowDef.level1,
    level2: rowDef.level2,
    level3: rowDef.level3,
    getValue: (monthRow) => findItemValue(
      monthRow,
      rowDef.level1,
      rowDef.level2,
      rowDef.level3,
    ),
  }))
);

const buildStudioBlocks = (
  params: IExportStaffRateExcelParams,
): IStudioBreakdownBlock<IStaffRateBreakdownMonthRow>[] => {
  const fixedRows = buildStaffRateFixedRows();
  // Portfolio Overview: nested studios[].series[]; shared row taxonomy per studio.
  if (params.studios?.length) {
    return params.studios.map((studio) => ({
      studioName: studio.studio_name || '',
      rawData: studio.series ?? [],
      fixedRows,
    }));
  }

  return [{
    studioName: params.studioName ?? '',
    rawData: params.rawData ?? [],
    fixedRows,
  }];
};

const STAFF_RATE_EXPORT_BASE_NAME = 'Studio Staff Rate';

const buildStaffRateBreakdownConfig = (
  params: IExportStaffRateExcelParams,
): IStudioBreakdownExcelConfig<IStaffRateBreakdownMonthRow> => {
  // File name: "Studio Staff Rate" or "Studio Staff Rate - Miniclip" on Studios page.
  const name = params.name
    ?? (params.studioName
      ? `${STAFF_RATE_EXPORT_BASE_NAME} - ${params.studioName}`
      : STAFF_RATE_EXPORT_BASE_NAME);

  return {
    name,
    sheetName: 'Staff Rate',
    noteRows: [
      { text: 'Quarterly Update', bold: true, trailingYears: true },
    ],
    infoHeaders: [
      'Studios',
      'Level 1 Cost Item',
      'Level 2 Cost Item',
      'Level 3 Cost Item',
    ],
    datatime: params.datatime,
    getMonthKey: (row) => row.date,
    // 1 = Actual, 0 = Forecast; omit/null → blank cell (do not default to Forecast).
    isActualMonth: (row) => {
      if (row.source === undefined || row.source === null) return null;
      return row.source === 'actual';
      // if (row.is_actual === undefined || row.is_actual === null) return null;
      // return row.is_actual === 1;
    },
    studioBlocks: buildStudioBlocks(params),
    // L2 labels are long; L3 is short ("Salary & Welfare") — invert HC's wide-D layout.
    infoWidths: [20, 22, 32, 22],
    // Level 2 "Staff Rate" → blue; add more rules here to highlight other labels.
    cellHighlights: [
      {
        column: 3,
        texts: ['Staff Rate'],
        color: 'FF0070C0',
        bold: true,
      },
    ],
  };
};

export const exportStaffRateExcel = async (
  params: IExportStaffRateExcelParams,
): Promise<void> => {
  const config = buildStaffRateBreakdownConfig(params);
  if (params.fileFormat === 'csv') {
    exportStudioBreakdownCsv(config);
    return;
  }
  await exportStudioBreakdownExcel(config);
};
